import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse } from '@ponzinomics/types';
import { apiClient, MOCK_MODE } from '../lib';
import { useCurrentProject } from './use-current-project';

const MOCK_API_KEYS: ApiKey[] = [
  {
    id: 'key-1',
    name: 'Development Key',
    keyPrefix: 'ponzinomics_test_',
    lastFour: 'ab12',
    environment: 'test',
    scopes: [],
    lastUsedAt: new Date().toISOString(),
    usageCount: 42,
    projectId: 'proj-1',
    userId: 'dev-user-1',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    expiresAt: null,
    revokedAt: null,
  },
  {
    id: 'key-2',
    name: 'Production Key',
    keyPrefix: 'ponzinomics_live_',
    lastFour: 'zx89',
    environment: 'live',
    scopes: [],
    lastUsedAt: new Date(Date.now() - 3600 * 1000).toISOString(),
    usageCount: 1337,
    projectId: 'proj-1',
    userId: 'dev-user-1',
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    expiresAt: null,
    revokedAt: null,
  },
];

// Query keys factory
export const apiKeyKeys = {
  all: ['api-keys'] as const,
  lists: () => [...apiKeyKeys.all, 'list'] as const,
  list: (projectId: string) => [...apiKeyKeys.lists(), projectId] as const,
};

// List API keys for the current project
export function useApiKeys() {
  const { projectId } = useCurrentProject();

  return useQuery({
    queryKey: apiKeyKeys.list(projectId!),
    queryFn: async (): Promise<ApiKey[]> => {
      if (MOCK_MODE) {
        return MOCK_API_KEYS;
      }
      return apiClient.get<ApiKey[]>(`/projects/${projectId}/api-keys`);
    },
    enabled: Boolean(projectId),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// Create a new API key
export function useCreateApiKey() {
  const queryClient = useQueryClient();
  const { projectId } = useCurrentProject();

  return useMutation({
    mutationFn: async (data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> => {
      if (MOCK_MODE) {
        const newKey: ApiKey = {
          id: `key-${Date.now()}`,
          name: data.name,
          keyPrefix: `ponzinomics_${data.environment ?? 'test'}_`,
          lastFour: Math.random().toString(36).slice(-4),
          environment: data.environment ?? 'test',
          scopes: data.scopes ?? [],
          lastUsedAt: null,
          usageCount: 0,
          projectId: projectId!,
          userId: 'dev-user-1',
          createdAt: new Date().toISOString(),
          expiresAt: data.expiresAt ?? null,
          revokedAt: null,
        };
        return {
          key: `ponzinomics_${data.environment ?? 'test'}_mock_${Math.random().toString(36).slice(2, 26)}`,
          apiKey: newKey,
        };
      }
      return apiClient.post<CreateApiKeyResponse>(`/projects/${projectId}/api-keys`, data);
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: apiKeyKeys.list(projectId) });
      }
    },
  });
}

// Revoke (delete) an API key
export function useRevokeApiKey() {
  const queryClient = useQueryClient();
  const { projectId } = useCurrentProject();

  return useMutation({
    mutationFn: async (keyId: string): Promise<string> => {
      if (MOCK_MODE) {
        return keyId;
      }
      await apiClient.delete(`/projects/${projectId}/api-keys/${keyId}`);
      return keyId;
    },
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: apiKeyKeys.list(projectId) });
      }
    },
  });
}
